/**
 * Convert hex color to RGB and calculate luminance
 * @param backgroundColor - Hex color string (e.g., '#FF0000')
 * @returns Luminance value between 0 and 1
 */
export var calculateLuminance = function calculateLuminance(backgroundColor) {
  if (!backgroundColor) return 0.5; // Default to medium luminance

  // Convert hex to RGB
  var hex = backgroundColor.replace("#", "");
  var r = parseInt(hex.substr(0, 2), 16);
  var g = parseInt(hex.substr(2, 2), 16);
  var b = parseInt(hex.substr(4, 2), 16);

  // Calculate luminance using the relative luminance formula
  // This formula gives more weight to green and less to blue, matching human eye sensitivity
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};

/**
 * Calculate text color based on background color brightness
 * @param backgroundColor - Hex color string (e.g., '#FF0000')
 * @returns Tailwind CSS class for appropriate text color
 */
export var getTextColorForBackground = function getTextColorForBackground(backgroundColor) {
  if (!backgroundColor) return "text-gray-800 dark:text-gray-200";
  var luminance = calculateLuminance(backgroundColor);

  // Return dark text for light backgrounds, light text for dark backgrounds
  return luminance > 0.5 ? "text-gray-900" : "text-white";
};

/**
 * Calculate border color based on background color brightness
 * @param backgroundColor - Hex color string (e.g., '#FF0000')
 * @returns Tailwind CSS class for appropriate border color
 */
export var getBorderColorForBackground = function getBorderColorForBackground(backgroundColor) {
  if (!backgroundColor) return "border-gray-500 dark:border-gray-500";
  var luminance = calculateLuminance(backgroundColor);

  // For borders, we want good contrast but not too harsh
  // Light backgrounds get darker borders, dark backgrounds get lighter borders
  if (luminance > 0.7) {
    // Very light backgrounds - use darker border for good contrast
    return "border-gray-600";
  } else if (luminance > 0.4) {
    // Medium backgrounds - use medium border
    return "border-gray-500";
  } else if (luminance > 0.2) {
    // Dark backgrounds - use lighter border
    return "border-gray-300";
  } else {
    // Very dark backgrounds - use light border for visibility
    return "border-gray-200";
  }
};

/**
 * Get border style object for custom colors (changed from background to border)
 * @param sectionColor - Hex color string
 * @returns Style object with borderColor or empty object
 */
export var getBorderStyle = function getBorderStyle(sectionColor) {
  if (sectionColor) {
    return {
      borderColor: sectionColor
    };
  }
  return {};
};

/**
 * Get border classes for default styling (provides appropriate background when using custom colors)
 * @param sectionColor - Hex color string
 * @returns Tailwind CSS classes for background
 */
export var getBorderClasses = function getBorderClasses(sectionColor, isLC, useDarkBackground) {
  if (isLC) {
    return useDarkBackground ? "bg-gray-300 dark:bg-gray-900" : "bg-gray-200 dark:bg-gray-800";
  }
  if (sectionColor) {
    // When using custom section colors, provide a subtle background for better readability
    return "bg-white dark:bg-gray-800";
  }
  return "bg-white dark:bg-gray-700"; // Default background classes
};

/**
 * Get border color for input fields based on section color
 * @param sectionColor - Hex color string
 * @returns Tailwind CSS class for border color
 */
export var getInputBorderColor = function getInputBorderColor(sectionColor, isLC) {
  if (!sectionColor || isLC) return "border-gray-500 dark:border-gray-500";

  // For input fields, we want to use the section color as the border color
  // but ensure it's visible and provides good contrast
  var luminance = calculateLuminance(sectionColor);

  // If the section color is very light, use a darker border for visibility
  if (luminance > 0.8) {
    return "border-gray-600";
  }

  // For most colors, use the section color as border
  return "border-2"; // Will be styled with the section color via inline styles
};

// Backward compatibility aliases
export var getBackgroundStyle = getBorderStyle;
export var getBackgroundClasses = getBorderClasses;

/**
 * Get text classes based on theme (no longer dependent on background color)
 * @param sectionColor - Hex color string (kept for backward compatibility but not used)
 * @returns Tailwind CSS classes for text color
 */
export var getTextClasses = function getTextClasses(sectionColor, isLC, useDarkBackground) {
  // Since we're no longer using background colors, always return appropriate text colors
  // for light and dark themes to ensure readability
  if (isLC) {
    return useDarkBackground ? "text-gray-800 dark:text-gray-100" : "text-gray-800 dark:text-gray-200";
  }
  return "text-gray-800 dark:text-gray-200";
};
export var PRESET_COLORS = {
  qure: ["#21C4E8", "#413AD4", "#808080", "#9A6612", "#A3D01C", "#B01070", "#C1DEC1", "#D9A6D5", "#E0873F", "#EA3CDC"]
};

/**
 * Preset colors that work well in both light and dark modes
 * Organized by color categories for easy selection
 */
export var PRESET_COLORS_SAMPLE = {
  // Primary brand colors
  brand: ["#00D0CD", "#008280", "#FF7869", "#E8414E"],
  // Blues and teals
  blues: ["#3B82F6", "#0EA5E9", "#06B6D4", "#0891B2"],
  teals: ["#0F766E", "#14B8A6", "#2DD4BF", "#5EEAD4"],
  // Greens
  greens: ["#10B981", "#059669", "#047857", "#065F46"],
  emeralds: ["#16A34A", "#15803D", "#166534", "#14532D"],
  // Purples and pinks
  purples: ["#8B5CF6", "#7C3AED", "#6D28D9", "#5B21B6"],
  pinks: ["#EC4899", "#DB2777", "#BE185D", "#9D174D"],
  // Oranges and yellows
  oranges: ["#F59E0B", "#D97706", "#B45309", "#92400E"],
  yellows: ["#EAB308", "#CA8A04", "#A16207", "#854D0E"],
  // Reds
  reds: ["#EF4444", "#DC2626", "#B91C1C", "#991B1B"],
  lightReds: ["#F87171", "#FCA5A5", "#FECACA", "#FEE2E2"],
  // Grays (neutral)
  grays: ["#6B7280", "#4B5563", "#374151", "#1F2937"],
  lightGrays: ["#9CA3AF", "#D1D5DB", "#E5E7EB", "#F3F4F6"],
  // Dark and light extremes
  extremes: ["#000000", "#FFFFFF"]
};

/**
 * Get all preset colors as a flat array
 * @returns Array of all preset colors
 */
export var getAllPresetColors = function getAllPresetColors() {
  return Object.values(PRESET_COLORS).flat();
};

/**
 * Get preset colors by category
 * @param category - The color category to get
 * @returns Array of colors for the specified category
 */
export var getPresetColorsByCategory = function getPresetColorsByCategory(category) {
  return PRESET_COLORS[category] || [];
};

/**
 * Get hover overlay style for custom colors
 * @param sectionColor - Hex color string
 * @returns Style object with backgroundColor or empty object
 */
export var getHoverOverlayStyle = function getHoverOverlayStyle(sectionColor) {
  if (sectionColor) {
    return {
      backgroundColor: "".concat(sectionColor, "E6")
    }; // 90% opacity for custom colors
  }
  return {}; // Use default classes
};

/**
 * Get hover overlay classes for default styling
 * @param sectionColor - Hex color string
 * @returns Tailwind CSS classes for hover overlay
 */
export var getHoverOverlayClasses = function getHoverOverlayClasses(sectionColor) {
  if (sectionColor) {
    return "backdrop-blur-sm"; // Only backdrop blur when using custom color
  }
  return "bg-white/90 dark:bg-gray-700/90 backdrop-blur-sm"; // Default classes
};

/**
 * Get loading overlay style for custom colors
 * @param sectionColor - Hex color string
 * @returns Style object with backgroundColor or empty object
 */
export var getLoadingOverlayStyle = function getLoadingOverlayStyle(sectionColor) {
  if (sectionColor) {
    return {
      backgroundColor: "".concat(sectionColor, "CC")
    }; // 80% opacity for custom colors
  }
  return {}; // Use default classes
};

/**
 * Get loading overlay classes for default styling
 * @param sectionColor - Hex color string
 * @returns Tailwind CSS classes for loading overlay
 */
export var getLoadingOverlayClasses = function getLoadingOverlayClasses(sectionColor) {
  if (sectionColor) {
    return "backdrop-blur-sm"; // Only backdrop blur when using custom color
  }
  return "bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm"; // Default classes
};